import { useCallback, useEffect, useState } from "react";
import { Loader2, ShieldPlus, Trash2, UserRoundCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { api, apiError, fmtWhen } from "@/lib/api";

/**
 * The portal's super-admins.
 *
 * The two the portal belongs to keep this list, and neither of them can be taken off it — by
 * anybody. A super-admin signs in on the standing passcode, exactly as they do, and the portal
 * never says on screen that such a passcode exists.
 */
export const SuperAdmins = () => {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [ask, setAsk] = useState(null);
  const [shut, setShut] = useState(false);

  const load = useCallback(() => {
    api
      .get("/super-admins")
      .then((r) => setData(r.data))
      .catch(() => setShut(true));
  }, []);

  useEffect(load, [load]);

  const add = async (e) => {
    e?.preventDefault();
    setBusy(true);
    try {
      const { data: out } = await api.post("/super-admins", { email: email.trim() });
      setData((d) => ({ ...d, rows: out.rows }));
      setEmail("");
      toast.success(`${out.added} is a super-admin now. ${out.note}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row) => {
    setBusy(true);
    try {
      const { data: out } = await api.delete(`/super-admins/${encodeURIComponent(row.email)}`);
      setData((d) => ({ ...d, rows: out.rows }));
      toast.success(`${out.removed} is no longer a super-admin. Their account is untouched.`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
      setAsk(null);
    }
  };

  if (shut) return null;
  if (!data)
    return (
      <div className="mt-6 flex h-20 items-center justify-center border border-border bg-card">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );

  return (
    <section className="mt-6 border border-border bg-card" data-testid="super-admins">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-4 py-3">
        <div>
          <h2 className="font-display text-base font-bold tracking-tight">
            The portal&apos;s super-admins
          </h2>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            A super-admin may edit, approve and remove anything in the portal while Super-Admin
            mode is on, and signs in the same way as every other officer.
          </p>
        </div>
        <span className="font-data text-[11px] text-muted-foreground" data-testid="super-admin-count">
          {data.rows.length} account(s)
        </span>
      </div>

      <div className="divide-y divide-border">
        {data.rows.map((row) => (
          <div
            key={row.email}
            className="flex flex-wrap items-center gap-3 px-4 py-3"
            data-testid={`super-admin-${row.email}`}
          >
            <UserRoundCheck
              className={`h-4 w-4 shrink-0 ${
                row.founder ? "text-[hsl(var(--healthy))]" : "text-muted-foreground"
              }`}
            />
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold">
                {row.name}
                {row.founder && (
                  <span
                    className="ml-2 border border-[hsl(var(--healthy))] px-1.5 py-0.5 font-display text-[9px] font-bold uppercase tracking-widest text-[hsl(var(--healthy))]"
                    data-testid={`super-admin-founder-${row.email}`}
                  >
                    Founder · cannot be removed
                  </span>
                )}
              </span>
              <span className="font-data block text-[10.5px] text-muted-foreground">
                {row.email}
                {row.posting ? ` · ${row.posting}` : ""}
                {row.last_login_at
                  ? ` · last in ${fmtWhen(row.last_login_at)}`
                  : " · has not signed in yet"}
                {row.added_by ? ` · added by ${row.added_by}` : ""}
              </span>
            </span>
            {data.can_manage && !row.founder && (
              <button
                type="button"
                disabled={busy}
                data-testid={`super-admin-remove-${row.email}`}
                onClick={() => setAsk(row)}
                className="flex items-center gap-1 border border-border px-2 py-1.5 font-display text-[9.5px] font-bold uppercase tracking-widest transition-colors duration-200 hover:border-[hsl(var(--critical))] hover:text-[hsl(var(--critical))] disabled:opacity-40"
              >
                <Trash2 className="h-3 w-3" /> Take off
              </button>
            )}
          </div>
        ))}
      </div>

      {data.can_manage ? (
        <form onSubmit={add} className="flex flex-wrap items-end gap-3 border-t border-border p-4">
          <label className="block">
            <span className="label-caps block">Make another officer a super-admin</span>
            <Input
              type="email"
              className="mt-1 h-11 w-[280px] rounded-none"
              placeholder="name@sail.in"
              data-testid="super-admin-email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button
            type="submit"
            disabled={busy || !email.trim()}
            data-testid="super-admin-add"
            className="flex h-11 items-center gap-2 bg-primary px-5 font-display text-[11px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-90 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldPlus className="h-4 w-4" />}
            Add
          </button>
          <p className="max-w-md text-[10.5px] leading-relaxed text-muted-foreground">
            If the officer has no portal account yet, one is opened for them as an SDTD
            administrator. An officer who already has an account keeps the role they have — the
            super-admin powers sit on top of it.
          </p>
        </form>
      ) : (
        <p
          className="border-t border-border p-4 text-[11px] text-muted-foreground"
          data-testid="super-admin-locked"
        >
          This list is kept by {data.founders.join(" and ")}. You can see it; only they can change
          it.
        </p>
      )}

      <ConfirmDialog
        open={!!ask}
        onOpenChange={(v) => !v && setAsk(null)}
        title={`Take ${ask?.name || ""} off the super-admins?`}
        description={`${ask?.email} keeps their portal account and their role. They lose the super-admin powers and the standing way in.`}
        confirmLabel="Take off"
        tone="danger"
        testId="confirm-remove-super-admin"
        onConfirm={() => remove(ask)}
      />
    </section>
  );
};
